import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Grade, StudentRecord } from '../types';
import { FiArrowLeft } from 'react-icons/fi';

const StudentRecordPage: React.FC = () => {
  const [grades, setGrades] = useState<Grade[]>([]);
  const [studentRecord, setStudentRecord] = useState<StudentRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) return;

      try {
        // Fetch grades
        const gradesRef = collection(db, 'grades');
        const gradesQuery = query(gradesRef, where('userId', '==', currentUser.uid));
        const gradesSnapshot = await getDocs(gradesQuery);
        const gradesData = gradesSnapshot.docs.map((doc) => ({
          gradeId: doc.id,
          ...doc.data(),
        })) as Grade[];

        // Sort by semester
        gradesData.sort((a, b) => a.semester.localeCompare(b.semester));
        setGrades(gradesData);

        // Fetch student record
        const recordRef = doc(db, 'studentRecords', currentUser.uid);
        const recordSnap = await getDoc(recordRef);

        if (recordSnap.exists()) {
          setStudentRecord({
            recordId: recordSnap.id,
            ...recordSnap.data(),
          } as StudentRecord);
        }
      } catch (error) {
        console.error('Error fetching student data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  // Group grades by semester
  const gradesBySemester = grades.reduce((acc, grade) => {
    if (!acc[grade.semester]) {
      acc[grade.semester] = [];
    }
    acc[grade.semester].push(grade);
    return acc;
  }, {} as Record<string, Grade[]>);

  // Academic calendar order: Fall YYYY → Winter YYYY+1 → Spring YYYY+1 → Summer YYYY+1
  // Assign each semester a sortable integer: year * 10 + season offset
  const semesterSortKey = (semester: string): number => {
    const [season = '', yearStr = '0'] = semester.split(' ');
    const year = parseInt(yearStr);
    switch (season) {
      case 'Fall':   return year * 10 + 0;
      case 'Winter': return year * 10 + 1; // Winter YYYY follows Fall YYYY
      case 'Spring': return year * 10 + 2;
      case 'Summer': return year * 10 + 3;
      default:       return year * 10;
    }
  };
  const sortedSemesters = Object.keys(gradesBySemester).sort(
    (a, b) => semesterSortKey(a) - semesterSortKey(b)
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-uc-navy text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/home')}
                className="mr-4 hover:text-uc-gold transition-colors flex items-center"
              >
                <FiArrowLeft size={20} className="mr-2" />
                Back
              </button>
              <h1 className="text-2xl font-bold font-header">My Student Record</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-uc-navy"></div>
            <p className="mt-2 text-gray-600">Loading student record...</p>
          </div>
        ) : (
          <>
            {/* Student Information */}
            {studentRecord && (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h2 className="text-xl font-bold text-uc-navy mb-4 font-header">
                  Student Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Major</p>
                    <p className="font-semibold text-uc-navy">{studentRecord.major}</p>
                  </div>
                  {studentRecord.minor && (
                    <div>
                      <p className="text-sm text-gray-600">Minor</p>
                      <p className="font-semibold text-uc-navy">{studentRecord.minor}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-600">Enrollment Status</p>
                    <p className="font-semibold text-uc-navy">
                      {studentRecord.enrollmentStatus}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Credits</p>
                    <p className="font-semibold text-uc-navy">{studentRecord.totalCredits}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Academic Advisor</p>
                    <p className="font-semibold text-uc-navy">{studentRecord.advisorName}</p>
                    <p className="text-sm text-gray-600">{studentRecord.advisorEmail}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Academic History */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-uc-navy mb-4 font-header">
                Academic History
              </h2>

              {Object.keys(gradesBySemester).length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-600">No grade history available</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Grades will appear here when added to Firestore
                  </p>
                </div>
              ) : (
                <>
                  {sortedSemesters.map((semester) => { const semesterGrades = gradesBySemester[semester]; return (
                    <div key={semester} className="mb-6">
                      <h3 className="text-lg font-semibold text-uc-navy mb-3 border-b-2 border-uc-gold pb-2">
                        {semester}
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                                Course Code
                              </th>
                              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                                Course Name
                              </th>
                              <th className="px-4 py-2 text-center text-sm font-semibold text-gray-700">
                                Credits
                              </th>
                              <th className="px-4 py-2 text-center text-sm font-semibold text-gray-700">
                                Grade
                              </th>
                              <th className="px-4 py-2 text-center text-sm font-semibold text-gray-700">
                                Grade Points
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {semesterGrades.map((grade) => (
                              <tr key={grade.gradeId} className="border-b hover:bg-gray-50">
                                <td className="px-4 py-3 font-semibold text-uc-navy text-sm">
                                  {grade.courseCode}
                                </td>
                                <td className="px-4 py-3 text-sm">{grade.courseName}</td>
                                <td className="px-4 py-3 text-center text-sm">
                                  {grade.credits}
                                </td>
                                <td className="px-4 py-3 text-center text-sm">
                                  <span className="font-semibold text-uc-navy">
                                    {grade.grade}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center text-sm">
                                  {Number(grade.gradePoints).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Semester Summary */}
                      <div className="mt-3 p-3 bg-gray-50 rounded flex justify-between">
                        <span className="font-semibold text-sm">Semester Total:</span>
                        <span className="font-semibold text-sm">
                          {semesterGrades.reduce((sum, g) => sum + g.credits, 0)} Credits
                        </span>
                      </div>
                    </div>
                  ); })}
                </>
              )}

              {/* GPA Information in Essay Format */}
              {studentRecord && Object.keys(gradesBySemester).length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-uc-navy mb-3 border-b-2 border-uc-gold pb-2">
                    Grade Point Average
                  </h3>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                    <div>
                      <span className="font-semibold text-gray-700">Cumulative GPA:</span>{' '}
                      <span className="text-uc-navy">3.90</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700">Academic Record:</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700">Term GPA:</span>{' '}
                      <span className="text-uc-navy">3.900</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700">Term Grade Points:</span>{' '}
                      <span className="text-uc-navy">46.80</span>
                    </div>
                    <div className="col-span-2">
                      <span className="font-semibold text-gray-700">Term Credits Passed:</span>{' '}
                      <span className="text-uc-navy">12.0</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default StudentRecordPage;
